import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux';
import { updateClinician } from '../redux/clinic/clinicSlice';
import useFetchDetails from '../hooks/Clinic/useFetchClinicianDetails';
import ClinicianAdminEdit from '../components/clinician/ClinicianAdminEdit';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

// Type for the clinician data expected by ClinicianAdminEdit
interface ClinicianData {
  _id: string;
  clinicianId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  emailSource: string;
  npiNumber: string;
  title: string;
  role: string;
  phone: string;
  specialties: string[];
  licenses: string[];
  slug: string;
  isActive: boolean;
  id_practice: string;
  createdAt: string;
  updatedAt: string;
  gender?: string;
  ethnicity?: string;
  experience?: string;
  education?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  profile: {
    detailsScrapedAt: string;
    detailsSource: string;
    editUrl: string;
    bio?: string;
    profileImage?: string;
    vslVideo?: string;
    rates?: Array<{ sessionType: string; amount: number; currency: string }>;
    sessionTypes?: Array<{ type: string; duration: number; rate?: number }>;
    insuranceAccepted?: string[];
    languages?: string[];
    modalities?: string[];
    ageRanges?: string[];
    sessionLength?: string;
    sessionPrice?: string;
  };
  availability?: {
    weeklySchedule?: {
      monday?: Array<{ start: string; end: string; available: boolean }>;
      tuesday?: Array<{ start: string; end: string; available: boolean }>;
      wednesday?: Array<{ start: string; end: string; available: boolean }>;
      thursday?: Array<{ start: string; end: string; available: boolean }>;
      friday?: Array<{ start: string; end: string; available: boolean }>;
      saturday?: Array<{ start: string; end: string; available: boolean }>;
      sunday?: Array<{ start: string; end: string; available: boolean }>;
    };
  };
}

export const ClinicianProfileEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedClinician, isLoading, error, fetchClinicianData } = useFetchDetails();
  
  // Add loading and feedback states
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (id) {
      fetchClinicianData(id);
    }
  }, [id, fetchClinicianData]);

  // Convert Clinician to ClinicianData format
  // IMPORTANT: Most fields are at ROOT level, NOT in profile object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const convertToClinicianData = (clinician: any): ClinicianData => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const profile: any = clinician.profile || {};
    
    // Handle availability - API returns calendar-based structure, but we need weeklySchedule for editing
    let availability = clinician.availability || {};
    if (!availability.weeklySchedule) {
      availability = {
        ...availability, // Preserve calendar data (open, booked, etc.)
        weeklySchedule: {
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
          sunday: []
        }
      };
    }
    
    // Handle rates - can be object {} or array
    let ratesArray: Array<{ sessionType: string; amount: number; currency: string }> = [];
    if (Array.isArray(clinician.rates)) {
      ratesArray = clinician.rates.map((r: unknown) => {
        const rate = r as { type?: string; sessionType?: string; amount?: string | number; currency?: string };
        return {
          sessionType: rate.type || rate.sessionType || 'Individual',
          amount: typeof rate.amount === 'string' ? parseFloat(rate.amount.replace(/[^0-9.]/g, '')) : (rate.amount || 0),
          currency: rate.currency || 'USD'
        };
      });
    } else if (clinician.rates && typeof clinician.rates === 'object') {
      // Convert rates object to array format
      const ratesObj = clinician.rates as Record<string, unknown>;
      Object.keys(ratesObj).forEach(key => {
        const amount = ratesObj[key];
        if (amount && (typeof amount === 'number' || typeof amount === 'string')) {
          ratesArray.push({
            sessionType: key === 'perIndividual' || key === 'perSession' ? 'Individual' : 
                        key === 'perCouple' ? 'Couples' :
                        key === 'perGroup' ? 'Group' :
                        key === 'perFamily' ? 'Family' : key,
            amount: typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount,
            currency: 'USD'
          });
        }
      });
    }
    
    return {
      _id: String(clinician._id || clinician.id || ''),
      clinicianId: String(clinician.clinicianId || clinician._id || clinician.id || ''),
      firstName: String(clinician.firstName || ''),
      lastName: String(clinician.lastName || ''),
      fullName: String(clinician.fullName || `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim()),
      email: String(clinician.email || ''),
      emailSource: String(clinician.emailSource || ''),
      npiNumber: String(clinician.npiNumber || ''),
      title: String(clinician.title || ''),
      role: String(clinician.role || ''),
      phone: String(clinician.phone || clinician.phone_number || ''),
      specialties: Array.isArray(clinician.specialties) 
        ? clinician.specialties.map((s: unknown) => typeof s === 'string' ? s : (s as { name?: string })?.name || String(s))
        : [],
      licenses: Array.isArray(clinician.licenses) ? clinician.licenses.map(String) : [],
      slug: String(clinician.slug || ''),
      isActive: Boolean(clinician.isActive),
      id_practice: String(clinician.id_practice || ''),
      createdAt: String(clinician.createdAt || ''),
      updatedAt: String(clinician.updatedAt || ''),
      gender: clinician.gender ? String(clinician.gender) : undefined,
      ethnicity: clinician.ethnicity ? String(clinician.ethnicity) : undefined,
      experience: clinician.experience ? String(clinician.experience) : undefined,
      education: Array.isArray(clinician.education) ? clinician.education.map(String) : [],
      address: clinician.address ? {
        street: clinician.address.street ? String(clinician.address.street) : undefined,
        city: clinician.address.city ? String(clinician.address.city) : undefined,
        state: clinician.address.state ? String(clinician.address.state) : undefined,
        zip: clinician.address.zip || clinician.zip_code ? String(clinician.address.zip || clinician.zip_code) : undefined
      } : (clinician.zip_code ? {
        street: undefined,
        city: undefined,
        state: undefined,
        zip: String(clinician.zip_code)
      } : undefined),
      profile: {
        detailsScrapedAt: String(profile.detailsScrapedAt || ''),
        detailsSource: String(profile.detailsSource || ''),
        editUrl: String(profile.editUrl || ''),
        // bio - check root level first, then profile (same as verification page)
        bio: clinician.bio || profile.bio ? String(clinician.bio || profile.bio) : '',
        profileImage: profile.profileImage || clinician.profileImage ? String(profile.profileImage || clinician.profileImage) : undefined,
        vslVideo: profile.vslVideo || profile.vsl_url || clinician.vslVideo || clinician.vsl_url ? String(profile.vslVideo || profile.vsl_url || clinician.vslVideo || clinician.vsl_url) : undefined,
        // rates - handle both root level object/array
        rates: ratesArray,
        // sessionTypes - check root level first, then profile (same as verification page)
        sessionTypes: Array.isArray(clinician.sessionTypes) || Array.isArray(profile.sessionTypes)
          ? (clinician.sessionTypes || profile.sessionTypes || [])
          : [],
        // insuranceAccepted - check root level first, then profile (same as verification page)
        insuranceAccepted: Array.isArray(clinician.insuranceAccepted) || Array.isArray(profile.insuranceAccepted)
          ? (clinician.insuranceAccepted || profile.insuranceAccepted || []).map(String)
          : [],
        // languages - check root level first, then profile (same as verification page)
        languages: Array.isArray(clinician.languages) || Array.isArray(profile.languages)
          ? (clinician.languages || profile.languages || ['English']).map(String)
          : ['English'],
        // modality - check root level first, then profile.treatmentModalities (same as verification page)
        modalities: Array.isArray(clinician.modality) || Array.isArray(profile.treatmentModalities)
          ? (clinician.modality || profile.treatmentModalities || []).map(String)
          : [],
        // ageRange - check root level first, then profile.preferredAges (same as verification page)
        ageRanges: Array.isArray(clinician.ageRange) || Array.isArray(profile.preferredAges)
          ? (clinician.ageRange || profile.preferredAges || []).map(String)
          : [],
        sessionLength: profile.sessionLength || clinician.sessionLength ? String(profile.sessionLength || clinician.sessionLength) : undefined,
        sessionPrice: profile.sessionPrice || clinician.sessionPrice ? String(profile.sessionPrice || clinician.sessionPrice) : undefined
      },
      availability: availability
    };
  };

  const handleSave = async (updatedClinician: ClinicianData) => {
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      // Format the data according to backend requirements
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        // Personal Information - Always include
        firstName: updatedClinician.firstName || '',
        lastName: updatedClinician.lastName || '',
        email: updatedClinician.email || '',
        emailSource: updatedClinician.emailSource || '',
        phone: updatedClinician.phone || '',
        phone_number: updatedClinician.phone || '',
        
        // Professional Information
        npiNumber: updatedClinician.npiNumber || '',
        title: updatedClinician.title || '',
        role: updatedClinician.role || '',
        bio: updatedClinician.profile?.bio || '',
        profileImage: updatedClinician.profile?.profileImage || '',
        vsl_url: updatedClinician.profile?.vslVideo || '',
        vslVideo: updatedClinician.profile?.vslVideo || '',
        gender: updatedClinician.gender || '',
        ethnicity: updatedClinician.ethnicity || '',
        experience: updatedClinician.experience || '',
        
        // Education - ensure it's an array
        education: Array.isArray(updatedClinician.education) && updatedClinician.education.length > 0 
          ? updatedClinician.education 
          : [],
        
        // Professional Arrays - Always send arrays (even if empty)
        specialties: Array.isArray(updatedClinician.specialties) ? updatedClinician.specialties : [],
        licenses: Array.isArray(updatedClinician.licenses) ? updatedClinician.licenses : [],
        
        // Profile Arrays - Professional Data
        languages: Array.isArray(updatedClinician.profile?.languages) && updatedClinician.profile.languages.length > 0
          ? updatedClinician.profile.languages
          : [],
        insuranceAccepted: Array.isArray(updatedClinician.profile?.insuranceAccepted) && updatedClinician.profile.insuranceAccepted.length > 0
          ? updatedClinician.profile.insuranceAccepted
          : [],
        modality: Array.isArray(updatedClinician.profile?.modalities) && updatedClinician.profile.modalities.length > 0
          ? updatedClinician.profile.modalities
          : [],
        ageRange: Array.isArray(updatedClinician.profile?.ageRanges) && updatedClinician.profile.ageRanges.length > 0
          ? updatedClinician.profile.ageRanges
          : [],
        sessionTypes: Array.isArray(updatedClinician.profile?.sessionTypes) && updatedClinician.profile.sessionTypes.length > 0
          ? updatedClinician.profile.sessionTypes
          : [],
        
        // Session Information
        sessionLength: updatedClinician.profile?.sessionLength || '',
        sessionPrice: updatedClinician.profile?.sessionPrice || '',
        
        // Status
        isActive: updatedClinician.isActive !== undefined ? updatedClinician.isActive : true,
        
        // Address object - also check zip_code at root level
        address: updatedClinician.address || (selectedClinician && (selectedClinician as any).zip_code) ? {
          street: updatedClinician.address?.street || '',
          city: updatedClinician.address?.city || '',
          state: updatedClinician.address?.state || '',
          zip: updatedClinician.address?.zip || (selectedClinician && (selectedClinician as any).zip_code ? String((selectedClinician as any).zip_code) : '')
        } : undefined,
        
        // Also send zip_code at root level for compatibility
        zip_code: updatedClinician.address?.zip || (selectedClinician && (selectedClinician as any).zip_code ? String((selectedClinician as any).zip_code) : undefined),
        
        // Rates object - transformed format for backend compatibility
        rates: updatedClinician.profile?.rates && updatedClinician.profile.rates.length > 0 ? {
          perSession: updatedClinician.profile.rates.find((r) => r.sessionType === 'Individual' || r.sessionType === 'individual')?.amount,
          perGroup: updatedClinician.profile.rates.find((r) => r.sessionType === 'Group' || r.sessionType === 'group')?.amount,
          perFamily: updatedClinician.profile.rates.find((r) => r.sessionType === 'Family' || r.sessionType === 'family')?.amount,
          perCouple: updatedClinician.profile.rates.find((r) => r.sessionType === 'Couple' || r.sessionType === 'couple')?.amount,
          perIndividual: updatedClinician.profile.rates.find((r) => r.sessionType === 'Individual' || r.sessionType === 'individual')?.amount
        } : undefined,
        
        // Also send rates array for direct access
        profileRates: Array.isArray(updatedClinician.profile?.rates) && updatedClinician.profile.rates.length > 0
          ? updatedClinician.profile.rates
          : undefined,
        
        // Availability - preserve calendar data (open, booked, etc.) and include weeklySchedule
        availability: selectedClinician && (selectedClinician as any).availability ? {
          ...((selectedClinician as any).availability), // Preserve existing calendar data (open, booked, cancelled, outOfOffice, blocked)
          weeklySchedule: updatedClinician.availability?.weeklySchedule || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          }
        } : {
          weeklySchedule: updatedClinician.availability?.weeklySchedule || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          }
        },
        
        // Skip invitation update flag
        skipInvitationUpdate: true
      };

      // Remove only null/undefined values, but keep empty strings and empty arrays for professional data
      Object.keys(updateData).forEach(key => {
        // Keep skipInvitationUpdate flag
        if (key === 'skipInvitationUpdate') {
          return; // Don't remove this flag
        }
        // Keep arrays even if empty for professional data
        if (Array.isArray(updateData[key])) {
          return; // Don't remove arrays
        }
        // Keep empty strings for required fields
        if (typeof updateData[key] === 'string' && updateData[key] === '') {
          // Only remove empty strings for optional fields
          const optionalFields = ['emailSource', 'ethnicity', 'experience', 'sessionLength', 'sessionPrice'];
          if (optionalFields.includes(key)) {
            delete updateData[key];
          }
          return;
        }
        // Remove undefined and null
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });


      if (!id) {
        throw new Error('Clinician ID is required');
      }
      await dispatch(updateClinician({ clinicianId: id, updateData })).unwrap();
      
      // Show success message
      setSaveMessage({ type: 'success', message: 'Clinician updated successfully!' });
      
      // Refresh data to reflect saved changes
      if (id) {
        await fetchClinicianData(id);
      }
      
    } catch (error) {
      console.error('Failed to update clinician:', error);
      setSaveMessage({ type: 'error', message: 'Failed to update clinician. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Loading clinician data...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !selectedClinician) {
    return (
      <DashboardLayout>
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Clinician not found.</p>
      </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Loading/Success/Error Messages */}
      {isSaving && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-md">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" />
            <div>
              <p className="font-medium">Updating Clinician...</p>
              <p className="text-sm opacity-90">Please wait while we save your changes</p>
            </div>
          </div>
        </div>
      )}

      {saveMessage && (
        <div className={`fixed top-4 right-4 px-6 py-4 rounded-lg shadow-lg z-50 max-w-md ${
          saveMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          <div className="flex items-center gap-3">
            {saveMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <div>
              <p className="font-medium">
                {saveMessage.type === 'success' ? 'Success!' : 'Error!'}
              </p>
              <p className="text-sm opacity-90">{saveMessage.message}</p>
            </div>
          </div>
      </div>
      )}

      {selectedClinician && (
        <ClinicianAdminEdit 
          clinician={convertToClinicianData(selectedClinician)} 
          onSave={handleSave}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      )}
    </DashboardLayout>
  );
};
